import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CaretLeft, CaretRight, Check } from "@phosphor-icons/react";

const STEPS = ["Basic Details", "Assignment", "Reminders & Links"];
const PRIORITIES = ["low", "normal", "high", "urgent", "critical"];
const ASSIGN_TYPES = [
  { v: "common", l: "Common Task", d: "Same task, only one final completion required" },
  { v: "individual", l: "Individual Completion", d: "Every member must submit their own response" },
  { v: "responsibility", l: "Responsibility-Wise", d: "Each member owns a separate responsibility" },
];

const label = "text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block";
const inputCls = "bg-zinc-950 border-zinc-800 rounded-sm text-zinc-100 focus:border-emerald-600 focus-visible:ring-emerald-600";

export default function TaskCreate() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [cats, setCats] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [labels, setLabels] = useState({});
  const [priorities, setPriorities] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    title: "", description: "", department: "", project: "", ref_code: "", customer_name: "", category: "",
    priority: "normal", start_at: "", due_at: "", expected_hours: "",
    location: "", tags: "", instructions: "", assignment_type: "common",
    assignees: [], primary_owner: "", reviewer: "", reporting_manager: "",
    watchers: [], parent_id: "", dependencies: [], time_mode: "calendar", escalation_hours: 2, custom: {},
    reminders: { before_due_hours: 24, at_due: true, overdue_freq_hours: 4, email: true, whatsapp: true, in_app: true },
  });

  useEffect(() => {
    api.get("/users?active=true").then((r) => setUsers(r.data));
    api.get("/departments").then((r) => setDepts(r.data));
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/tasks?view=all&parent=root").then((r) => setAllTasks(r.data));
    api.get("/settings/form-labels").then((r) => setLabels(r.data)).catch(() => {});
    api.get("/priorities").then((r) => setPriorities(r.data)).catch(() => {});
    api.get("/settings/custom-fields").then((r) => setCustomFields(r.data.fields || [])).catch(() => {});
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const toggleAssignee = (u) => {
    setF((p) => {
      const exists = p.assignees.find((a) => a.user_id === u.id);
      const assignees = exists
        ? p.assignees.filter((a) => a.user_id !== u.id)
        : [...p.assignees, { user_id: u.id, name: u.name, responsibility: "", deadline: "" }];
      return { ...p, assignees };
    });
  };

  const setResp = (uid, field, val) =>
    setF((p) => ({ ...p, assignees: p.assignees.map((a) => (a.user_id === uid ? { ...a, [field]: val } : a)) }));

  const toggleArr = (key, id) =>
    setF((p) => ({ ...p, [key]: p[key].includes(id) ? p[key].filter((x) => x !== id) : [...p[key], id] }));

  const submit = async () => {
    if (!f.title.trim()) { toast.error("Task title is required"); setStep(0); return; }
    if (f.assignees.length === 0) { toast.error("Assign at least one team member"); setStep(1); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        expected_hours: f.expected_hours ? parseFloat(f.expected_hours) : null,
        start_at: f.start_at ? new Date(f.start_at).toISOString() : null,
        due_at: f.due_at ? new Date(f.due_at).toISOString() : null,
        tags: f.tags ? f.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        primary_owner: f.primary_owner || (f.assignees[0]?.user_id ?? null),
        parent_id: f.parent_id || null,
        assignees: f.assignees.map((a) => ({
          ...a, deadline: a.deadline ? new Date(a.deadline).toISOString() : null,
        })),
      };
      const { data } = await api.post("/tasks", payload);
      toast.success("Task created & assigned");
      navigate(`/tasks/${data.id}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const SelectField = ({ value, onChange, items, placeholder, testid }) => (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className={inputCls} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
        {items.map((i) => (
          <SelectItem key={i.value} value={i.value} className="capitalize">{i.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="p-6 lg:p-8 max-w-3xl animate-fade-in">
      <button onClick={() => navigate("/tasks")} className="text-sm text-zinc-500 hover:text-emerald-400 flex items-center gap-1 mb-4 transition-colors">
        <CaretLeft size={14} /> Back to tasks
      </button>
      <h1 className="font-heading text-3xl font-black tracking-tight text-zinc-50 mb-6">Create Task</h1>

      <div className="flex mb-8 border border-zinc-800">
        {STEPS.map((s, i) => (
          <div key={s} className={`flex-1 px-4 py-3 flex items-center gap-2 text-sm border-r border-zinc-800 last:border-r-0 ${i === step ? "bg-emerald-600/10 text-emerald-400" : i < step ? "text-emerald-400 bg-zinc-900" : "text-zinc-500 bg-zinc-950"}`}>
            <span className={`w-5 h-5 flex items-center justify-center text-[11px] font-bold rounded-sm ${i === step ? "bg-emerald-600 text-white" : i < step ? "bg-emerald-500 text-white" : "bg-zinc-800"}`}>
              {i < step ? <Check size={12} weight="bold" /> : i + 1}
            </span>
            <span className="hidden sm:inline">{s}</span>
          </div>
        ))}
      </div>

      <div className="bg-zinc-900/30 border border-zinc-800 p-6 space-y-5">
        {step === 0 && (
          <>
            <div>
              <label className={label}>Task Title *</label>
              <Input value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="task-title" placeholder="e.g. Complete Panel GA Drawing" />
            </div>
            <div>
              <label className={label}>Description</label>
              <Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className={inputCls} rows={3} data-testid="task-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={label}>{labels.department || "Department"}</label>
                <SelectField value={f.department} onChange={(v) => set("department", v)} placeholder="Select" testid="task-department" items={depts.map((d) => ({ value: d.name, label: d.name }))} />
              </div>
              <div>
                <label className={label}>{labels.category || "Category"}</label>
                <SelectField value={f.category} onChange={(v) => set("category", v)} placeholder="Select" testid="task-category" items={cats.map((c) => ({ value: c.name, label: c.name }))} />
              </div>
              <div>
                <label className={label}>{labels.project || "Project / Customer"}</label>
                <Input value={f.project} onChange={(e) => set("project", e.target.value)} className={inputCls} data-testid="task-project" placeholder="Project or customer" />
              </div>
              <div>
                <label className={label}>{labels.ref_code || "Ref Code"}</label>
                <Input value={f.ref_code} onChange={(e) => set("ref_code", e.target.value)} className={inputCls} data-testid="task-ref-code" placeholder="Reference code" />
              </div>
              <div>
                <label className={label}>{labels.customer_name || "Customer Name"}</label>
                <Input value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={inputCls} data-testid="task-customer-name" placeholder="Customer name" />
              </div>
              <div>
                <label className={label}>{labels.priority || "Priority"}</label>
                <SelectField value={f.priority} onChange={(v) => set("priority", v)} placeholder="Priority" testid="task-priority" items={(priorities.length ? priorities.map((p) => ({ value: p.name.toLowerCase(), label: p.name })) : PRIORITIES.map((p) => ({ value: p, label: p })))} />
              </div>
              <div>
                <label className={label}>Start Date & Time</label>
                <input type="datetime-local" value={f.start_at} onChange={(e) => set("start_at", e.target.value)} data-testid="task-start" className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:border-emerald-600 focus:outline-none [color-scheme:dark]" />
              </div>
              <div>
                <label className={label}>Due Date & Time</label>
                <input type="datetime-local" value={f.due_at} onChange={(e) => set("due_at", e.target.value)} data-testid="task-due" className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:border-emerald-600 focus:outline-none [color-scheme:dark]" />
              </div>
              <div>
                <label className={label}>Expected Working Hours</label>
                <Input type="number" value={f.expected_hours} onChange={(e) => set("expected_hours", e.target.value)} className={inputCls} data-testid="task-hours" placeholder="e.g. 8" />
              </div>
              <div>
                <label className={label}>Location</label>
                <Input value={f.location} onChange={(e) => set("location", e.target.value)} className={inputCls} placeholder="Shop floor / office" />
              </div>
            </div>
            <div>
              <label className={label}>Tags (comma separated)</label>
              <Input value={f.tags} onChange={(e) => set("tags", e.target.value)} className={inputCls} placeholder="vcb, 11kv, urgent" />
            </div>
            <div>
              <label className={label}>Instructions</label>
              <Textarea value={f.instructions} onChange={(e) => set("instructions", e.target.value)} className={inputCls} rows={2} />
            </div>
            {customFields.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                {customFields.map((cf) => (
                  <div key={cf.key}>
                    <label className={label}>{cf.label}</label>
                    <Input value={(f.custom && f.custom[cf.key]) || ""} onChange={(e) => set("custom", { ...(f.custom || {}), [cf.key]: e.target.value })} className={inputCls} data-testid={`custom-${cf.key}`} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {step === 1 && (
          <>
            <div>
              <label className={label}>Assignment Method</label>
              <div className="grid gap-2">
                {ASSIGN_TYPES.map((a) => (
                  <button key={a.v} onClick={() => set("assignment_type", a.v)} data-testid={`assign-type-${a.v}`}
                    className={`text-left p-3 border rounded-sm transition-colors ${f.assignment_type === a.v ? "border-emerald-600 bg-emerald-600/10" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"}`}>
                    <div className={`text-sm font-semibold ${f.assignment_type === a.v ? "text-emerald-400" : "text-zinc-200"}`}>{a.l}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{a.d}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={label}>Assign Team Members *</label>
              <div className="border border-zinc-800 max-h-60 overflow-y-auto divide-y divide-zinc-800">
                {users.map((u) => {
                  const sel = f.assignees.find((a) => a.user_id === u.id);
                  return (
                    <div key={u.id} className="p-3">
                      <label className="flex items-center gap-3 cursor-pointer" data-testid={`assignee-${u.id}`}>
                        <Checkbox checked={!!sel} onCheckedChange={() => toggleAssignee(u)} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" />
                        <div className="flex-1">
                          <span className="text-sm text-zinc-100">{u.name}</span>
                          <span className="text-xs text-zinc-500 ml-2">{u.department} · {u.designation || u.role}</span>
                        </div>
                      </label>
                      {sel && f.assignment_type === "responsibility" && (
                        <div className="mt-2 ml-7 grid grid-cols-2 gap-2">
                          <Input value={sel.responsibility} onChange={(e) => setResp(u.id, "responsibility", e.target.value)} placeholder="Responsibility" className={`${inputCls} h-8 text-xs`} />
                          <input type="datetime-local" value={sel.deadline} onChange={(e) => setResp(u.id, "deadline", e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 h-8 text-xs text-zinc-100 [color-scheme:dark]" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={label}>Primary Owner</label>
                <SelectField value={f.primary_owner} onChange={(v) => set("primary_owner", v)} placeholder="Auto (first)" testid="task-owner" items={f.assignees.map((a) => ({ value: a.user_id, label: a.name }))} />
              </div>
              <div>
                <label className={label}>Reviewer / Approver</label>
                <SelectField value={f.reviewer} onChange={(v) => set("reviewer", v)} placeholder="Select" testid="task-reviewer" items={users.filter((u) => u.role !== "member").map((u) => ({ value: u.id, label: u.name }))} />
              </div>
              <div>
                <label className={label}>Reporting Manager</label>
                <SelectField value={f.reporting_manager} onChange={(v) => set("reporting_manager", v)} placeholder="Select" items={users.filter((u) => u.role !== "member").map((u) => ({ value: u.id, label: u.name }))} />
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div>
              <label className={label}>Time Calculation Mode</label>
              <SelectField value={f.time_mode} onChange={(v) => set("time_mode", v)} placeholder="Mode"
                items={[{ value: "calendar", label: "Calendar Hours" }, { value: "working", label: "Working Hours (excl. holidays)" }]} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={label}>Reminder before due (hours)</label>
                <Input type="number" value={f.reminders.before_due_hours} onChange={(e) => set("reminders", { ...f.reminders, before_due_hours: +e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>Overdue reminder every (hours)</label>
                <Input type="number" value={f.reminders.overdue_freq_hours} onChange={(e) => set("reminders", { ...f.reminders, overdue_freq_hours: +e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>Escalate to manager after (hours overdue)</label>
                <Input type="number" value={f.escalation_hours} onChange={(e) => set("escalation_hours", +e.target.value)} className={inputCls} data-testid="task-escalation" />
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              {[["email", "Email"], ["whatsapp", "WhatsApp"], ["in_app", "In-App"], ["at_due", "Alert at due time"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                  <Checkbox checked={f.reminders[k]} onCheckedChange={(v) => set("reminders", { ...f.reminders, [k]: v })} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" />
                  {l}
                </label>
              ))}
            </div>
            <div>
              <label className={label}>Parent Task (make this a subtask)</label>
              <SelectField value={f.parent_id} onChange={(v) => set("parent_id", v === "none" ? "" : v)} placeholder="None"
                items={[{ value: "none", label: "None" }, ...allTasks.map((t) => ({ value: t.id, label: `${t.ref_code || t.customer_name || "—"} · ${t.title}` }))]} />
            </div>
            <div>
              <label className={label}>Dependencies (this task waits for)</label>
              <div className="border border-zinc-800 max-h-40 overflow-y-auto divide-y divide-zinc-800">
                {allTasks.length === 0 && <div className="p-3 text-xs text-zinc-500">No other tasks yet.</div>}
                {allTasks.map((t) => (
                  <label key={t.id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-zinc-900">
                    <Checkbox checked={f.dependencies.includes(t.id)} onCheckedChange={() => toggleArr("dependencies", t.id)} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" />
                    <span className="font-mono text-[11px] text-zinc-500">{t.ref_code || t.customer_name || "—"}</span>
                    <span className="text-sm text-zinc-300 truncate">{t.title}</span>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="flex justify-between mt-6">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="border-zinc-800 bg-zinc-900 rounded-sm gap-1" data-testid="step-back">
          <CaretLeft size={14} /> Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={() => setStep((s) => s + 1)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold gap-1" data-testid="step-next">
            Next <CaretRight size={14} />
          </Button>
        ) : (
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold gap-1" data-testid="submit-task">
            {saving ? "Creating…" : "Create Task"} <Check size={16} weight="bold" />
          </Button>
        )}
      </div>
    </div>
  );
}
